import { cn } from '@/utils/cn';
import { MessageSquare } from 'lucide-react';

export type FollowUpSuggestion = {
  text: string;
  id: string;
};

type FollowUpParseResult = {
  cleanContent: string;
  suggestions: FollowUpSuggestion[];
};

export function stripSystemReminder(content: string): string {
  if (!content) return content;
  // Remove complete system-reminder blocks
  let cleaned = content.replace(/<system-reminder>[\s\S]*?<\/system-reminder>/gi, '').trim();
  // Remove any trailing incomplete system-reminder blocks
  cleaned = cleaned.replace(/<system-reminder>[\s\S]*$/gi, '').trim();
  // Remove complete systemContext blocks
  cleaned = cleaned.replace(/<systemContext>[\s\S]*?<\/systemContext>/gi, '').trim();
  // Remove any trailing incomplete systemContext blocks
  cleaned = cleaned.replace(/<systemContext>[\s\S]*$/gi, '').trim();
  return cleaned;
}

export function extractFollowUpSuggestions(content: string): FollowUpParseResult {
  const sanitized = stripSystemReminder(content);
  const blockRegex = /<follow_up_suggestions>[\s\S]*?<\/follow_up_suggestions>/i;
  const match = sanitized.match(blockRegex);
  if (!match) {
    return { cleanContent: sanitized, suggestions: [] };
  }

  const block = match[0];
  const suggestionRegex = /<suggestion>([\s\S]*?)<\/suggestion>/gi;
  const suggestions: FollowUpSuggestion[] = [];
  let suggestionMatch: RegExpExecArray | null;
  while ((suggestionMatch = suggestionRegex.exec(block)) !== null) {
    const text = suggestionMatch[1].replace(/\s+/g, ' ').trim();
    if (text) {
      suggestions.push({
        text,
        id: `suggestion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      });
    }
  }

  const cleanContent = sanitized.replace(blockRegex, '').trim();
  return { cleanContent, suggestions };
}

interface FollowUpSuggestionsProps {
  suggestions: FollowUpSuggestion[];
  disabled?: boolean;
  onSelect: (suggestion: string) => void;
  className?: string;
}

export function FollowUpSuggestions({ suggestions, disabled, onSelect, className }: FollowUpSuggestionsProps) {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-1 mt-2', className)}>
      <div className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground mb-1">
        <MessageSquare className="h-3 w-3" />
        <span>Follow Up Suggestions</span>
      </div>

      <div className="flex flex-col divide-y divide-border/60">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            type="button"
            onClick={() => onSelect(suggestion.text)}
            disabled={disabled}
            className={cn(
              'group relative w-full text-left py-2 px-1',
              'hover:bg-accent/10 transition-all duration-200',
              'focus:outline-none focus-visible:bg-accent/15',
              'active:scale-[0.995]',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  'text-xs text-foreground/80 leading-snug',
                  'group-hover:text-foreground transition-colors duration-200',
                )}
              >
                {suggestion.text}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
